import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  PlusIcon,
  SparklesIcon,
  XMarkIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import { useFrame } from '../engine'
import { iso } from '../v3/iso'
import { EXKURSE, EXKURS_BY_ID } from './exkurse'

const idList = PropTypes.arrayOf(PropTypes.string).isRequired

// ---------- Hotspots im Büro ----------

/**
 * Pulsierende Punkte in der Welt. Sichtbar nur, solange die Geschichte an
 * der passenden Station steht, damit sie nicht im Weg sind.
 */
export function Hotspots({ seen, onOpen }) {
  const [visible, setVisible] = useState('')

  useFrame((l) => {
    const ids = EXKURSE.flatMap((ex) =>
      ex.spots
        .filter((s) => l >= s.at[0] && l <= s.at[1])
        .map((s) => `${ex.id}:${s.key}`)
    ).join(',')
    setVisible((prev) => (prev === ids ? prev : ids))
  })

  const on = visible ? visible.split(',') : []

  return (
    <div className='ix-spots'>
      {EXKURSE.flatMap((ex) =>
        ex.spots.map((s) => {
          const p = iso(...s.at3)
          const active = on.includes(`${ex.id}:${s.key}`)
          return (
            <div
              key={`${ex.id}:${s.key}`}
              className='off-anchor'
              style={{ left: p.x, top: p.y }}
            >
              <button
                type='button'
                className={clsx(
                  'ix-spot',
                  active && 'is-on',
                  seen.includes(ex.id) && 'is-seen',
                  s.side === 'left' && 'ix-spot--left'
                )}
                tabIndex={active ? 0 : -1}
                aria-hidden={!active}
                onClick={() => onOpen(ex.id)}
              >
                <span className='ix-spot__dot'>
                  {seen.includes(ex.id) ? (
                    <CheckIcon width={18} height={18} />
                  ) : (
                    <PlusIcon width={20} height={20} />
                  )}
                </span>
                <span className='ix-spot__label'>{ex.short}</span>
              </button>
            </div>
          )
        })
      )}
    </div>
  )
}

Hotspots.propTypes = { seen: idList, onOpen: PropTypes.func.isRequired }

// ---------- Menü mit allen Exkursen ----------

export function ExkursMenu({ seen, onOpen }) {
  const [open, setOpen] = useState(false)
  const wrap = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const close = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false)
    }
    const esc = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  return (
    <div ref={wrap} className='ix-menu'>
      <button
        type='button'
        className='ix-menu__toggle'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <SparklesIcon width={16} height={16} />
        Mehr entdecken
        <span className='ix-menu__count'>
          {seen.length}/{EXKURSE.length}
        </span>
      </button>
      {open && (
        <ul className='ix-menu__list'>
          {EXKURSE.map((ex) => (
            <li key={ex.id}>
              <button
                type='button'
                onClick={() => {
                  setOpen(false)
                  onOpen(ex.id)
                }}
              >
                <span
                  className={clsx(
                    'ix-menu__mark',
                    seen.includes(ex.id) && 'is-seen'
                  )}
                >
                  {seen.includes(ex.id) && <CheckIcon width={12} height={12} />}
                </span>
                <span>
                  <b>{ex.title}</b>
                  <small>{ex.teaser}</small>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

ExkursMenu.propTypes = { seen: idList, onOpen: PropTypes.func.isRequired }

// ---------- Dialog mit Szene und Schritten ----------

export function ExkursDialog({ id, onClose, onOpen }) {
  const ex = id ? EXKURS_BY_ID[id] : null
  const [step, setStep] = useState(0)
  const closeRef = useRef(null)
  const lastFocus = useRef(null)

  // Neuer Exkurs startet immer bei Schritt 1, Scrollen ist solange gesperrt
  useEffect(() => {
    if (!ex) return undefined
    setStep(0)
    lastFocus.current = document.activeElement
    const html = document.documentElement
    const prev = html.style.overflow
    html.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => {
      html.style.overflow = prev
      lastFocus.current?.focus?.()
    }
  }, [ex])

  useEffect(() => {
    if (!ex) return undefined
    const last = ex.steps.length - 1
    const key = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setStep((s) => Math.min(last, s + 1))
      if (e.key === 'ArrowLeft') setStep((s) => Math.max(0, s - 1))
    }
    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [ex, onClose])

  if (!ex) return null

  const last = ex.steps.length - 1
  const current = ex.steps[step]
  const next = EXKURSE[(EXKURSE.indexOf(ex) + 1) % EXKURSE.length]
  const { Scene } = ex

  return (
    <div className='ix-dialog' role='presentation'>
      <button
        type='button'
        className='ix-dialog__backdrop'
        aria-label='Exkurs schließen'
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        className='ix-dialog__card'
        role='dialog'
        aria-modal='true'
        aria-labelledby='ix-dialog-title'
      >
        <div className='ix-dialog__scene'>
          <Scene step={step} />
        </div>
        <div className='ix-dialog__text'>
          <div className='ix-dialog__top'>
            <span className='off-eyebrow'>{ex.eyebrow}</span>
            <button
              ref={closeRef}
              type='button'
              className='ix-dialog__close'
              onClick={onClose}
              aria-label='Exkurs schließen'
            >
              <XMarkIcon width={20} height={20} />
            </button>
          </div>
          <h3 id='ix-dialog-title' className='ix-dialog__title'>
            {ex.title}
          </h3>

          <div className='ix-dialog__step' key={step} aria-live='polite'>
            <div className='ix-dialog__n'>
              Schritt {step + 1} von {ex.steps.length}
            </div>
            <strong>{current.title}</strong>
            <p>{current.text}</p>
          </div>

          <div className='ix-dialog__dots'>
            {ex.steps.map((s, i) => (
              <button
                key={s.title}
                type='button'
                className={clsx('ix-dialog__dot', i === step && 'is-active')}
                onClick={() => setStep(i)}
                aria-label={`Schritt ${i + 1}: ${s.title}`}
              />
            ))}
          </div>

          <div className='ix-dialog__actions'>
            <button
              type='button'
              className='off-cta off-cta--ghost ix-btn'
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
            >
              <ArrowLeftIcon width={16} height={16} /> Zurück
            </button>
            {step < last ? (
              <button
                type='button'
                className='off-cta ix-btn'
                onClick={() => setStep((s) => s + 1)}
              >
                Weiter <ArrowRightIcon width={16} height={16} />
              </button>
            ) : (
              <button
                type='button'
                className='off-cta ix-btn'
                onClick={onClose}
              >
                Zurück zur Geschichte
              </button>
            )}
          </div>

          {step === last && (
            <button
              type='button'
              className='ix-dialog__next'
              onClick={() => onOpen(next.id)}
            >
              Nächster Exkurs: {next.title}{' '}
              <ArrowRightIcon width={14} height={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

ExkursDialog.propTypes = {
  id: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired
}
